import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

// Filter + sort state that parameterizes a paginated list query key. Mirrors
// `DataGridQuery` from `@/hooks/use-data-grid`; kept structural to avoid a cycle.
interface ListKeyFilters {
  search?: string;
  status?: string;
  sort?: string;
  sortDir?: string;
}

// --- WIDGET_FEATURE_START ---
// Reference entity (src/examples/widget). One factory per domain; mirror this
// shape for a real entity and invalidate via `*.all` after mutations.
export const widgetKeys = {
  all: ["widgets"] as const,
  list: (filters: ListKeyFilters) => ["widgets", "list", filters] as const,
  detail: (id: string) => ["widgets", "detail", id] as const,
};
// --- WIDGET_FEATURE_END ---

// --- S3_FEATURE_START ---
export const fileKeys = {
  all: ["files"] as const,
  list: (filters: ListKeyFilters) => ["files", "list", filters] as const,
  detail: (id: string) => ["files", "detail", id] as const,
} as const;
// --- S3_FEATURE_END ---

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const accessKeys = {
  all: ["access"] as const,
  /** The signed-in user's own level. Keyed by user so a sign-in as someone
   *  else on the same tab never reads the previous person's answer. */
  me: (userId: string) => ["my-access", userId] as const,
  list: (filters: ListKeyFilters) => ["access", "list", filters] as const,
} as const;

/** How many rows an Aggregate user's book has in a range, before search/filters. */
export const bookKeys = {
  count: (kind: "accounts" | "projects", range: string) => ["book-count", kind, range] as const,
} as const;

export const devKeys = {
  seedAccounts: ["dev", "seed-accounts"] as const,
} as const;

export const emailKeys = {
  all: ["email"] as const,
  outbox: () => ["email", "outbox"] as const,
  templates: () => ["email", "templates"] as const,
  template: (key: string) => ["email", "templates", key] as const,
} as const;

export const auditKeys = {
  all: ["audit"] as const,
  list: (filters: ListKeyFilters) => ["audit", "list", filters] as const,
} as const;

export const queryKeys = {
  accounts: (query: unknown, page: unknown) => ["accounts", query, page] as const,
  accountDetail: (accountId: string) => ["accounts", "detail", accountId] as const,
  projects: (query: unknown, page: unknown) => ["projects", query, page] as const,
  projectDetail: (demandId: string) => ["projects", "detail", demandId] as const,
};

/** The open month's T&M board. No range: it is always the month in progress. */
export const monthToDateKeys = {
  board: ["month-to-date"] as const,
} as const;
